import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getSchoolUrl } from "@/lib/slug";
import { useAuth } from "@/context/AuthContext";
import { Bell, MessageSquare, Sparkles } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function NotificationsBell() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!user) { setData({ items: [], unread_count: 0 }); return; }
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    load();
    if (!user) return;
    const t = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(t);
  }, [user, load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const markOne = async (notifId) => {
    // Fire-and-forget: PATCH marks single notification read; we optimistically
    // patch local state so the badge & unread highlight update instantly even
    // if the request is inflight when the user navigates away.
    setData((prev) => {
      const items = prev.items.map((n) => (n.notif_id === notifId && !n.read ? { ...n, read: true } : n));
      const unread_count = Math.max(0, (prev.unread_count || 0) - 1);
      return { ...prev, items, unread_count };
    });
    try { await api.patch(`/notifications/${notifId}/read`); } catch { /* silent */ }
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notifications-trigger"
          className="relative w-10 h-10 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-colors duration-200"
          aria-label="notifikasi"
        >
          <Bell size={16} />
          {data.unread_count > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E07A5F] text-white text-[10px] font-semibold inline-flex items-center justify-center border-2 border-[#1E3A8A]"
            >
              {data.unread_count > 9 ? "9+" : data.unread_count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[420px] overflow-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifikasi</span>
          {data.unread_count > 0 && (
            <button
              type="button"
              onClick={markAll}
              data-testid="notifications-mark-all-read"
              className="text-xs text-[#E07A5F] hover:underline"
            >
              Tandai semua sudah dibaca
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.items.length === 0 && (
          <div className="p-4 text-sm text-[#4B5563]" data-testid="notif-empty">
            Belum ada notifikasi. Simpan sekolah ke wishlist untuk dapat pemberitahuan.
          </div>
        )}
        {data.items.map((n, i) => (
          <Link
            key={n.notif_id}
            to={n.link_url || getSchoolUrl({ school_id: n.school_id, name: n.school_name })}
            data-testid={`notif-item-${i}`}
            onClick={() => { setOpen(false); if (!n.read) markOne(n.notif_id); }}
            className={"block p-3 border-b border-[#EAE7E0] last:border-b-0 hover:bg-[#F3F1ED] transition-colors " + (!n.read ? "bg-[#EEF2FF]" : "bg-white")}
          >
            <div className="flex items-start gap-2">
              <span className="mt-0.5 text-[#1E3A8A]">
                {n.kind === "summary" ? <Sparkles size={14} /> : <MessageSquare size={14} />}
              </span>
              <div className="flex-1 min-w-0">
                {(n.ruang_name || n.school_name) && (
                  <div className="text-xs uppercase tracking-widest text-[#4B5563]">{n.ruang_name || n.school_name}</div>
                )}
                <div className="text-sm text-[#1F2937] mt-0.5 leading-snug">{n.headline}</div>
                {n.body && <div className="text-xs text-[#4B5563] mt-0.5 leading-snug line-clamp-2">{n.body}</div>}
                <div className="text-xs text-[#4B5563] mt-1">{new Date(n.created_at).toLocaleString("id-ID")}</div>
              </div>
            </div>
          </Link>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
